import React from 'react';
import { IntlShape } from 'react-intl';
import isDesktop from '../../utils/sizeHelpers';
import abbreviateText from '../../utils/abbreviateText';
import {
  alphabeticalSorterFor,
  chronologicalSorterFor,
  getRenderLink,
  renderDateTime,
} from '../shared/TableLayout';
import { renderText } from '../shared/DrawerLayout/renderText';
import FuelTransactionType from '../shared/FuelTransactionType';
import FuelTransactionFlagged from '../shared/FuelTransactionFlagged';
import LinkToVehicle from '../shared/LinkToVehicle';
import LinkToTrip from '../shared/LinkToTrip';
import LinkToUser from '../shared/LinkToUser';
import { formatFuelAmount, formatFuelLevel, formatLitres } from '../../utils';
import { FuelTransaction } from '../../redux/store/userData/fuelTransactions/initialState';

type getColumnsArgs = {
  intl: IntlShape;
};

const numericSorterFor = (target: string) => (a: any, b: any) =>
  (a[target] ?? -Infinity) - (b[target] ?? -Infinity);

const getColumns = ({ intl }: getColumnsArgs) => {
  const title = (key: string, defaultMessage: string) =>
    intl.formatMessage({
      id: `fleetadmin.fuelTransactions.table.${key}`,
      defaultMessage,
    });

  return [
    {
      title: title('uniqueid', 'No (transaction)'),
      dataIndex: 'uniqueId',
      width: isDesktop() ? 200 : 150,
      ellipsis: true,
      fixed: 'left' as 'left',
      render: getRenderLink(),
      sorter: alphabeticalSorterFor('uniqueId'),
    },
    {
      title: title('type', 'Type'),
      dataIndex: 'type',
      width: 150,
      ellipsis: true,
      render: (type: string) => <FuelTransactionType type={type} />,
      sorter: alphabeticalSorterFor('type'),
    },
    {
      title: title('flagged', 'Flagged'),
      dataIndex: 'flagged',
      width: 150,
      ellipsis: true,
      render: (flagged: boolean) => (
        <FuelTransactionFlagged flagged={flagged} />
      ),
      sorter: (a: FuelTransaction, b: FuelTransaction) =>
        Number(!!a.flagged) - Number(!!b.flagged),
    },
    {
      title: title('timestamp', 'Date'),
      dataIndex: 'timestamp',
      width: 150,
      ellipsis: true,
      render: renderDateTime,
      sorter: chronologicalSorterFor('timestamp'),
    },
    {
      title: title('fuelstationbrandname', 'Station'),
      dataIndex: 'fuelStationBrandName',
      width: 150,
      ellipsis: true,
      render: renderText,
      sorter: alphabeticalSorterFor('fuelStationBrandName'),
    },
    {
      title: title('fuelstationaddress', 'Address'),
      dataIndex: 'fuelStationAddress',
      width: 250,
      ellipsis: true,
      render: (address: string) =>
        address ? (
          <span title={address}>{abbreviateText(address, 30)}</span>
        ) : (
          '-'
        ),
      sorter: alphabeticalSorterFor('fuelStationAddress'),
    },
    {
      title: title('plate', 'Plate'),
      dataIndex: 'plate',
      width: 150,
      ellipsis: true,
      render: (plate: string, record: FuelTransaction) =>
        plate ? <LinkToVehicle carId={record.carId!} text={plate} /> : '-',
      sorter: alphabeticalSorterFor('plate'),
    },
    {
      title: title('code', 'Code (trip)'),
      dataIndex: 'code',
      width: 150,
      ellipsis: true,
      render: (code: string, record: FuelTransaction) =>
        code ? (
          <LinkToTrip tripCode={code} tripId={record.tripId!} service={null} />
        ) : (
          '-'
        ),
      sorter: alphabeticalSorterFor('code'),
    },
    {
      title: title('userreferralcode', 'Code (user)'),
      dataIndex: 'userReferralCode',
      width: 150,
      ellipsis: true,
      render: (userReferralCode: string, record: FuelTransaction) =>
        userReferralCode ? (
          <LinkToUser userId={record.userId!} userCode={userReferralCode} />
        ) : (
          '-'
        ),
      sorter: alphabeticalSorterFor('userReferralCode'),
    },
    {
      title: title('merchantname', 'Merchant'),
      dataIndex: 'merchantName',
      width: 200,
      ellipsis: true,
      render: (merchantName: string) =>
        merchantName ? (
          <span title={merchantName}>{abbreviateText(merchantName, 20)}</span>
        ) : (
          '-'
        ),
      sorter: alphabeticalSorterFor('merchantName'),
    },
    {
      title: title('product', 'Product'),
      dataIndex: 'product',
      width: 150,
      ellipsis: true,
      render: renderText,
      sorter: alphabeticalSorterFor('product'),
    },
    {
      title: title('quantity', 'Quantity'),
      dataIndex: 'quantity',
      width: 150,
      ellipsis: true,
      render: formatLitres,
      sorter: numericSorterFor('quantity'),
    },
    {
      title: title('amounttotal', 'Gross amount'),
      dataIndex: 'amountTotal',
      width: 150,
      ellipsis: true,
      render: formatFuelAmount,
      sorter: numericSorterFor('amountTotal'),
    },
    {
      title: title('fuellevel', 'Fuel level'),
      dataIndex: 'fuelLevel',
      width: 150,
      ellipsis: true,
      render: (_: unknown, record: FuelTransaction) =>
        formatFuelLevel(record.fuelLevelBefore, record.fuelLevelAfter),
      sorter: numericSorterFor('fuelLevelBefore'),
    },
    {
      title: title('netamount', 'Net amount'),
      dataIndex: 'netAmount',
      width: 150,
      ellipsis: true,
      render: formatFuelAmount,
      sorter: numericSorterFor('netAmount'),
    },
    {
      title: title('vatamount', 'VAT amount'),
      dataIndex: 'vatAmount',
      width: 150,
      ellipsis: true,
      render: formatFuelAmount,
      sorter: numericSorterFor('vatAmount'),
    },
    {
      title: title('fleetname', 'Fleet'),
      dataIndex: 'fleetName',
      width: 150,
      ellipsis: true,
      render: renderText,
      sorter: alphabeticalSorterFor('fleetName'),
    },
    {
      title: title('brandid', 'Brand'),
      dataIndex: 'brandId',
      width: 150,
      ellipsis: true,
      render: (_: unknown, record: FuelTransaction) =>
        renderText(record.brandName),
      sorter: alphabeticalSorterFor('brandName'),
    },
    {
      title: title('merchantid', 'Merchant ID'),
      dataIndex: 'merchantId',
      width: 150,
      ellipsis: true,
      render: renderText,
      sorter: alphabeticalSorterFor('merchantId'),
    },
  ];
};

export default getColumns;
